import {
  ChangeDetectionStrategy,
  Component,
  Input,
  OnChanges,
} from '@angular/core';
import { isObservable, Observable, observableOf, rx } from '@shared/rx';
import {
  filter,
  get,
  includes,
  isEmpty,
  isString,
  keys,
  pick,
  values,
  without,
} from 'lodash';
import { APIActions } from '@store/actions';
import { StateService } from '@shared/services';
import { ELLIPSIS_STRING, ENTITY_ID_PATH } from '@config/global-constants';
import { isObjectIdValid } from '@shared/utils';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

interface IEntityCaptionDataFull {
  entity: string;
  entity_id?: string;
  entity_ids?: string[];
}

export type EntityCaptionData =
  | IEntityCaptionDataFull
  | string
  | string[]
  | Observable<string>;

@Component({
  selector: 'xvi-entity-caption',
  template: `
    <ng-container *ngIf="isHtml; else elseTemplate">
      <span [innerHtml]="sanitizeHTMLStringValue(captionObs | async)"> </span>
    </ng-container>
    <ng-template #elseTemplate>
      {{ captionObs | async }}
    </ng-template>
  `,
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class XVIEntityCaptionComponent implements OnChanges {
  @Input() public data: EntityCaptionData;
  @Input() public entity: string;
  public captionObs: Observable<string>;
  public isHtml: boolean = false;

  public constructor(
    private apiActions: APIActions,
    private stateService: StateService,
    private sanitizer: DomSanitizer,
  ) {}

  public ngOnChanges(): void {
    const entityType = get(this.data, 'entity', this.entity);
    if (!entityType) {
      if (isString(this.data)) {
        this.isHtml = includes(this.data, '<svg');
        this.captionObs = observableOf(this.data);
      }
      if (isObservable(this.data)) {
        this.captionObs = this.data;
      }

      return;
    }

    const visibleIds = this.getIdsToDisplay();

    this.captionObs = this.stateService
      .select(['entities', entityType, 'itemCaptions'])
      .pipe(
        rx.map((entityCaptions = {}) => {
          const captions = values(pick(entityCaptions, visibleIds));
          const missingIds = without(visibleIds, ...keys(entityCaptions));

          if (!isEmpty(missingIds)) {
            this.apiActions.requestEntitiesCaptions(entityType, missingIds);
          }

          return isEmpty(captions) ? ELLIPSIS_STRING : captions.join(', ');
        }),
      );
  }

  public sanitizeHTMLStringValue(value: string): SafeHtml {
    return this.sanitizer.bypassSecurityTrustHtml(value);
  }

  private getIdsToDisplay(): string[] {
    const entityIds = [].concat(
      get(this.data, 'entity_ids'),
      get(this.data, ENTITY_ID_PATH, this.data),
    );

    return filter(entityIds, isObjectIdValid);
  }
}
